import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, Activity, Layers, Sprout, MousePointerClick, Send, Target, Sparkles,
  ArrowUpRight, ArrowDownRight, AlertCircle, RefreshCw, Filter, Calendar, X,
  MessageCircle, Mail, MessageSquare, Globe, Link2, FlaskConical, IndianRupee, Info, Search,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer,
  AreaChart, Area, CartesianGrid,
} from 'recharts';

import Toast from '../components/Toast';
import useAutoLogout from '../hooks/useAutoLogout';
import { useTheme } from '../components/Navbar';
import API_BASE from '../config';

/* ----------------------------------------------------------------- theme -- */
const TK = {
  light: {
    page: '#f7f5ff',
    card: '#ffffff', cardAlt: '#fbfaff', border: '#e8e4f3', borderSoft: '#f0edf9',
    text: '#15131f', sub: '#5b5870', faint: '#8b8898', fainter: '#b0adbb',
    accent: '#5B4EE8', accentSoft: 'rgba(91,78,232,0.10)',
    grid: '#eeedf5', hover: 'rgba(0,0,0,0.025)', skelA: '#ede9fa', skelB: '#f5f3fd',
    good: '#059669', bad: '#dc2626', warn: '#d97706',
    shadow: '0 1px 3px rgba(91,78,232,0.08)',
  },
  dark: {
    page: '#080808',
    card: '#0f0f0f', cardAlt: '#0c0c0c', border: '#1f1f1f', borderSoft: '#161616',
    text: '#f5f5f5', sub: '#a3a3a3', faint: '#737373', fainter: '#525252',
    accent: '#7C72FF', accentSoft: 'rgba(124,114,255,0.15)',
    grid: '#1c1c1c', hover: 'rgba(255,255,255,0.03)', skelA: '#1a1a1a', skelB: '#262626',
    good: '#34d399', bad: '#f87171', warn: '#fbbf24',
    shadow: '0 1px 3px rgba(0,0,0,0.4)',
  },
};

/* -------------------------------------------------------------- channels -- */
const CHANNEL_META = {
  whatsapp: { label: 'WhatsApp', color: '#25D366', Icon: MessageCircle },
  email: { label: 'Email', color: '#3b82f6', Icon: Mail },
  sms: { label: 'SMS', color: '#f59e0b', Icon: MessageSquare },
  web: { label: 'Web', color: '#8b5cf6', Icon: Globe },
};
function channelMeta(ch) {
  const k = (ch || '').toLowerCase();
  return CHANNEL_META[k] || { label: ch || 'Other', color: '#7c3aed', Icon: Link2 };
}

/* --------------------------------------------------------------- helpers -- */
function dayStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function rangeFor(preset, customFrom, customTo) {
  const today = new Date(); today.setHours(0,0,0,0);
  const tNext = new Date(today); tNext.setDate(today.getDate()+1);
  if (preset === 'custom' && customFrom && customTo) {
    const ct = new Date(customTo); ct.setDate(ct.getDate()+1);
    const days = Math.max(1, Math.round((new Date(customTo)-new Date(customFrom))/864e5)+1);
    return { from: customFrom, to: dayStr(ct), days };
  }
  const days = preset === '7d' ? 7 : preset === '90d' ? 90 : 30;
  const f = new Date(today); f.setDate(today.getDate()-(days-1));
  return { from: dayStr(f), to: dayStr(tNext), days };
}
function prevRange(from, days) {
  const f = new Date(from);
  const pf = new Date(f); pf.setDate(f.getDate()-days);
  return { from: dayStr(pf), to: from };
}
function pctDelta(now, prev) {
  if (prev==null) return null;
  if (prev===0) return now>0 ? 100 : 0;
  return Math.round(((now-prev)/prev)*1000)/10;
}
function inr(n) { return (Number(n)||0).toLocaleString('en-IN'); }
function formatIST(d) {
  if (!d) return '—';
  const s = d.toString();
  const normalized = s.endsWith('Z') ? s : s.replace(' ','T')+'Z';
  const dt = new Date(normalized);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleString('en-IN',{
    timeZone:'Asia/Kolkata', day:'2-digit', month:'short', year:'numeric',
    hour:'2-digit', minute:'2-digit', hour12:true,
  });
}

/* ------------------------------------------------------------ primitives -- */
function useCountUp(target, dur=850) {
  const [v, setV] = useState(0);
  const ref = useRef(0);
  useEffect(() => {
    const start = performance.now();
    const from = ref.current;
    let raf;
    const tick = (now) => {
      const p = Math.min((now-start)/dur,1);
      const eased = 1-Math.pow(1-p,3);
      setV(from+(target-from)*eased);
      if (p<1) raf = requestAnimationFrame(tick);
      else ref.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, dur]);
  return v;
}
function AnimatedNumber({ value, decimals=0, prefix='', suffix='' }) {
  const v = useCountUp(Number(value)||0);
  const formatted = decimals>0 ? v.toFixed(decimals) : Math.round(v).toLocaleString('en-IN');
  return <span>{prefix}{formatted}{suffix}</span>;
}
function Skel({ h=40, w='100%', r=12, T }) {
  return (
    <div className="attr-skel" style={{ height:h, width:w, borderRadius:r,
      background:`linear-gradient(90deg,${T.skelA} 25%,${T.skelB} 50%,${T.skelA} 75%)`,
      backgroundSize:'800px 100%', animation:'attrShimmer 1.4s ease-in-out infinite' }} />
  );
}
function Delta({ value, pp=false, T }) {
  if (value==null) return null;
  const up = value>=0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="inline-flex items-center gap-0.5 text-[11px] font-bold" style={{ color: up?T.good:T.bad }}>
      <Icon className="w-3 h-3" />{Math.abs(value)}{pp?'pp':'%'}
    </span>
  );
}
function Donut({ segments, size=130, stroke=16, T }) {
  const total = segments.reduce((a,s)=>a+s.value,0);
  const r = (size-stroke)/2;
  const c = 2*Math.PI*r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.grid} strokeWidth={stroke} />
      {total>0 && segments.map((s) => {
        const dash = (s.value/total)*c;
        const el = (
          <circle key={s.label} cx={size/2} cy={size/2} r={r} fill="none"
            stroke={s.color} strokeWidth={stroke}
            strokeDasharray={`${dash} ${c-dash}`} strokeDashoffset={-offset}
            transform={`rotate(-90 ${size/2} ${size/2})`} strokeLinecap="butt" />
        );
        offset += dash;
        return el;
      })}
    </svg>
  );
}

/* =========================================================================
   ATTRIBUTION PAGE
   ========================================================================= */
export default function Attribution() {
  const navigate = useNavigate();
  useAutoLogout();
  const { theme } = useTheme();
  const T = TK[theme] || TK.light;

  const getToken = () => {
    try {
      const u = JSON.parse(localStorage.getItem('martech_user') || sessionStorage.getItem('martech_user') || '{}');
      return u.token || localStorage.getItem('martech_token') || sessionStorage.getItem('martech_token') || null;
    } catch { return localStorage.getItem('martech_token') || null; }
  };

  const [tab, setTab] = useState('channels');
  const [preset, setPreset] = useState('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [compare, setCompare] = useState(false);

  const [links, setLinks] = useState([]);
  const [overview, setOverview] = useState(null);
  const [conv, setConv] = useState(null);
  const [prevOverview, setPrevOverview] = useState(null);
  const [prevConv, setPrevConv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const range = useMemo(() => rangeFor(preset, customFrom, customTo), [preset, customFrom, customTo]);

  const fetchAll = useCallback(async () => {
    const token = getToken();
    if (!token) { navigate('/'); return; }
    setLoading(true); setError(null);
    const { from, to, days } = range;
    const authH = { Authorization: `Bearer ${token}` };
    const J = (u) => fetch(u, { headers: authH }).then((r) => r.json()).catch(() => null);

    const calls = [
      J(`${API_BASE}/api/v1/links/attribution?campaign_id=all`),
      J(`${API_BASE}/api/v1/attribution-hub/overview?from=${from}&to=${to}`),
      J(`${API_BASE}/api/v1/attribution-hub/conversions?from=${from}&to=${to}&window=30`),
    ];
    if (compare) {
      const pr = prevRange(from, days);
      calls.push(J(`${API_BASE}/api/v1/attribution-hub/overview?from=${pr.from}&to=${pr.to}`));
      calls.push(J(`${API_BASE}/api/v1/attribution-hub/conversions?from=${pr.from}&to=${pr.to}&window=30`));
    }

    const res = await Promise.allSettled(calls);
    const val = (i) => (res[i]?.status === 'fulfilled' ? res[i].value : null);

    const l = val(0);
    if (l) {
      const rows = Array.isArray(l.data) ? l.data
        : Array.isArray(l.links) ? l.links
        : Array.isArray(l) ? l : [];
      setLinks(rows);
    } else { setLinks([]); }

    setOverview(val(1));
    setConv(val(2));
    setPrevOverview(compare ? val(3) : null);
    setPrevConv(compare ? val(4) : null);

    if (res.every((x) => x.status === 'rejected')) setError('Network error — could not load attribution');
    setLoading(false);
  }, [navigate, range, compare]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* ---- derived ---- */
  const campaigns = useMemo(() => {
    const s = new Set();
    links.forEach((r) => { const c = r.campaign||r.campaign_name; if (c) s.add(c); });
    return Array.from(s);
  }, [links]);

  const filteredLinks = useMemo(() => links.filter((r) => {
    const ch = (r.channel||'').toLowerCase();
    const cmp = r.campaign||r.campaign_name||'';
    if (channelFilter !== 'all' && ch !== channelFilter) return false;
    if (campaignFilter !== 'all' && cmp !== campaignFilter) return false;
    return true;
  }), [links, channelFilter, campaignFilter]);

  const clicksByChannel = useMemo(() => {
    const acc = {};
    filteredLinks.forEach((r) => {
      const ch = (r.channel||'unknown').toLowerCase();
      acc[ch] = (acc[ch]||0) + parseInt(r.clicks ?? r.click_count ?? 0, 10);
    });
    return acc;
  }, [filteredLinks]);

  const channelRows = useMemo(() => {
    const ovChannels = overview?.channels || [];
    const keys = new Set([...Object.keys(clicksByChannel), ...ovChannels.map((c)=>c.channel)]);
    const sendsMap = {};
    ovChannels.forEach((c) => { sendsMap[c.channel] = c; });
    return Array.from(keys)
      .filter((k) => channelFilter === 'all' || k === channelFilter)
      .map((k) => {
        const s = sendsMap[k] || { sends_real:0, sends_dry_run:0, sends_total:0 };
        const clicks = clicksByChannel[k]||0;
        const ctr = s.sends_real>0 ? Math.round((clicks/s.sends_real)*1000)/10 : null;
        return { channel:k, clicks, sends_real:s.sends_real||0, sends_dry_run:s.sends_dry_run||0, sends_total:s.sends_total||0, ctr };
      })
      .sort((a,b) => (b.clicks+b.sends_total)-(a.clicks+a.sends_total));
  }, [overview, clicksByChannel, channelFilter]);

  /* chart data — explicit color per entry for recharts Cell */
  const channelChartData = useMemo(() =>
    channelRows.map((r) => ({ ...r, name: channelMeta(r.channel).label, fill: channelMeta(r.channel).color })),
  [channelRows]);

  const campaignClicks = useMemo(() => {
    const acc = {};
    filteredLinks.forEach((r) => {
      const c = r.campaign||r.campaign_name||'unattributed';
      acc[c] = (acc[c]||0) + parseInt(r.clicks ?? r.click_count ?? 0, 10);
    });
    return Object.entries(acc).map(([campaign,clicks])=>({campaign,clicks})).sort((a,b)=>b.clicks-a.clicks).slice(0,8);
  }, [filteredLinks]);

  const totalClicks = useMemo(() => Object.values(clicksByChannel).reduce((a,b)=>a+b,0), [clicksByChannel]);
  const ovTotals = overview?.totals || { sends_real:0, sends_dry_run:0, sends_total:0 };
  const convTotals = conv?.totals || { conversions:0, value:0, organic_conversions:0, organic_value:0, paid_conversions:0, paid_value:0, organic_share_pct:0 };
  const ctrOverall = ovTotals.sends_real>0 ? Math.round((totalClicks/ovTotals.sends_real)*1000)/10 : null;

  const prevOv = prevOverview?.totals || null;
  const prevCv = prevConv?.totals || null;
  const dSends = compare && prevOv ? pctDelta(ovTotals.sends_real, prevOv.sends_real) : null;
  const dConv = compare && prevCv ? pctDelta(convTotals.conversions, prevCv.conversions) : null;
  const dOrganic = compare && prevCv ? Math.round((convTotals.organic_share_pct - prevCv.organic_share_pct)*10)/10 : null;

  const filtersActive = channelFilter !== 'all' || campaignFilter !== 'all';

  const tooltipStyle = { background:T.card, border:`1px solid ${T.border}`, borderRadius:12, color:T.text, fontSize:12, boxShadow:T.shadow };
  const axisTick = { fill:T.faint, fontSize:11 };

  /* ===== render ===== */
  return (
    <div style={{ background:T.page, color:T.text, minHeight:'100vh', width:'100%' }}>
      <style>{`
        @keyframes attrShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        .attr-tab  { transition:all .16s ease; }
        .attr-row  { transition:background .14s ease; }
        .attr-row:hover { background:${T.hover}; }
        .attr-pill { transition:all .16s ease; }
      `}</style>

      <div className="h-14 md:hidden" />
      {toast && <Toast message={toast.msg} variant={toast.variant} onDismiss={()=>setToast(null)} />}

      <main className="px-4 md:px-6 py-6 pt-20 md:pt-8 max-w-[1180px] mx-auto">

        {/* header */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background:T.accentSoft }}>
              <TrendingUp className="w-5 h-5" style={{ color:T.accent }} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tight" style={{ color:T.text }}>Attribution</h1>
              <p className="text-sm mt-0.5 hidden md:block" style={{ color:T.faint }}>
                Channels, tracked links, and the organic conversions other tools miss
              </p>
            </div>
          </div>
          <button onClick={fetchAll}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold attr-pill"
            style={{ background:T.card, border:`1px solid ${T.border}`, color:T.sub }}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading?'animate-spin':''}`} />
            <span className="hidden md:inline">Refresh</span>
          </button>
        </div>

        {/* filters */}
        <div className="rounded-2xl p-3 md:p-3.5 mb-5 flex items-center gap-2 flex-wrap"
          style={{ background:T.card, border:`1px solid ${T.border}` }}>
          <div className="flex items-center gap-1.5 mr-1" style={{ color:T.faint }}>
            <Filter className="w-3.5 h-3.5" />
            <span className="text-[10px] font-bold uppercase tracking-wider hidden md:inline">Filters</span>
          </div>

          {/* date presets */}
          <div className="flex items-center gap-0.5 p-1 rounded-xl" style={{ background:T.cardAlt, border:`1px solid ${T.borderSoft}` }}>
            <Calendar className="w-3.5 h-3.5 ml-1.5" style={{ color:T.faint }} />
            {['7d','30d','90d','custom'].map((p) => (
              <button key={p} onClick={()=>setPreset(p)}
                className="attr-pill px-2.5 py-1 rounded-lg text-xs font-bold"
                style={preset===p ? {background:T.accent,color:'#fff'} : {background:'transparent',color:T.sub}}>
                {p==='custom'?'Custom':p}
              </button>
            ))}
          </div>

          {preset==='custom' && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <input type="date" value={customFrom} onChange={(e)=>setCustomFrom(e.target.value)}
                className="px-2.5 py-1.5 rounded-lg text-xs font-semibold outline-none"
                style={{ background:T.cardAlt, border:`1px solid ${T.borderSoft}`, color:T.text }} />
              <span style={{ color:T.faint }}>→</span>
              <input type="date" value={customTo} onChange={(e)=>setCustomTo(e.target.value)}
                className="px-2.5 py-1.5 rounded-lg text-xs font-semibold outline-none"
                style={{ background:T.cardAlt, border:`1px solid ${T.borderSoft}`, color:T.text }} />
            </div>
          )}

          <select value={channelFilter} onChange={(e)=>setChannelFilter(e.target.value)}
            className="px-3 py-1.5 rounded-xl text-xs font-bold outline-none cursor-pointer"
            style={{ background:T.cardAlt, border:`1px solid ${T.borderSoft}`, color:T.text }}>
            <option value="all">All channels</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="web">Web</option>
          </select>

          <select value={campaignFilter} onChange={(e)=>setCampaignFilter(e.target.value)}
            className="px-3 py-1.5 rounded-xl text-xs font-bold outline-none cursor-pointer"
            style={{ background:T.cardAlt, border:`1px solid ${T.borderSoft}`, color:T.text, maxWidth:180 }}>
            <option value="all">All campaigns</option>
            {campaigns.map((c)=><option key={c} value={c}>{c}</option>)}
          </select>

          <button onClick={()=>setCompare((v)=>!v)}
            className="attr-pill flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold"
            style={compare
              ? {background:T.accentSoft, color:T.accent, border:`1px solid ${T.accent}`}
              : {background:T.cardAlt, color:T.sub, border:`1px solid ${T.borderSoft}`}}>
            <Activity className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Compare prev. period</span>
            <span className="md:hidden">Compare</span>
          </button>

          {filtersActive && (
            <button onClick={()=>{setChannelFilter('all');setCampaignFilter('all');}}
              className="attr-pill flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-bold"
              style={{ color:T.faint }}>
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-3 px-5 py-4 rounded-2xl mb-5 text-sm font-semibold"
            style={{ background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.22)', color:T.bad }}>
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={fetchAll} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold" style={{ color:T.bad }}>
              <RefreshCw className="w-3 h-3" /> Retry
            </button>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          {[
            { icon:MousePointerClick, label:'Link Clicks',    val:totalClicks,                    delta:null,    pp:false, suffix:''  },
            { icon:Send,              label:'Real Sends',     val:ovTotals.sends_real,             delta:dSends,  pp:false, suffix:''  },
            { icon:Target,            label:'CTR',            val:ctrOverall??0,                   delta:null,    pp:false, suffix:'%', dash:ctrOverall==null },
            { icon:Sparkles,          label:'Conversions',    val:convTotals.conversions,          delta:dConv,   pp:false, suffix:''  },
            { icon:Sprout,            label:'Organic Share',  val:convTotals.organic_share_pct,    delta:dOrganic,pp:true,  suffix:'%' },
          ].map((k) => (
            <div key={k.label} className="rounded-2xl p-4" style={{ background:T.card, border:`1px solid ${T.border}`, boxShadow:T.shadow }}>
              <div className="flex items-center justify-between mb-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background:T.accentSoft }}>
                  <k.icon className="w-4 h-4" style={{ color:T.accent }} />
                </div>
                {compare && <Delta value={k.delta} pp={k.pp} T={T} />}
              </div>
              <div className="text-2xl font-black tracking-tight" style={{ color:T.text }}>
                {loading ? <Skel h={28} w={64} T={T} /> : k.dash ? '—'
                  : <AnimatedNumber value={k.val} suffix={k.suffix} decimals={k.suffix==='%'?1:0} />}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-wider mt-1" style={{ color:T.faint }}>{k.label}</div>
            </div>
          ))}
        </div>

        {/* tabs */}
        <div className="flex items-center gap-1 mb-5 p-1 rounded-xl w-fit"
          style={{ background:T.card, border:`1px solid ${T.border}` }}>
          {[
            { id:'channels', label:'Channels',            Icon:Activity  },
            { id:'tracked',  label:'Tracked Links',       Icon:Layers    },
            { id:'organic',  label:'Organic Conversions', Icon:Sprout    },
          ].map((t) => (
            <button key={t.id} onClick={()=>setTab(t.id)}
              className="attr-tab flex items-center gap-2 px-3 md:px-3.5 py-2 rounded-lg text-xs font-bold"
              style={tab===t.id ? {background:T.accent,color:'#fff'} : {background:'transparent',color:T.sub}}>
              <t.Icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>

        {/* ===================== CHANNELS TAB ===================== */}
        {tab==='channels' && (
          <>
            {/* chart */}
            <div className="rounded-2xl p-5 md:p-6 mb-4" style={{ background:T.card, border:`1px solid ${T.border}`, boxShadow:T.shadow }}>
              <h2 className="text-sm font-black mb-1" style={{ color:T.text }}>Clicks vs Real Sends by Channel</h2>
              <p className="text-xs mb-4" style={{ color:T.faint }}>Real sends are colored per channel; dry-run (simulated) sends shown separately in grey.</p>
              {loading ? <Skel h={220} T={T} /> : channelChartData.length===0 ? (
                <EmptyMini T={T} icon={Activity} text="No channel activity in this range" />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={channelChartData} margin={{ top:4, right:12, left:-10, bottom:4 }} barGap={4} barCategoryGap="30%">
                    <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false} />
                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill:T.hover }} />
                    <Bar dataKey="sends_real" name="Real sends" radius={[5,5,0,0]} isAnimationActive={false}>
                      {channelChartData.map((entry) => (
                        <Cell key={`real-${entry.channel}`} fill={entry.fill} />
                      ))}
                    </Bar>
                    <Bar dataKey="sends_dry_run" name="Dry-run" radius={[5,5,0,0]} fill={T.fainter} fillOpacity={0.55} isAnimationActive={false} />
                    <Bar dataKey="clicks" name="Clicks" radius={[5,5,0,0]} fill={T.accent} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <Legend swatch={T.fainter} label="Dry-run sends" T={T} />
                <Legend swatch={T.accent} label="Clicks" T={T} />
                <span className="text-[11px] font-bold" style={{ color:T.faint }}>Real sends colored per channel:</span>
                {channelChartData.map((r) => (
                  <Legend key={r.channel} swatch={r.fill} label={r.name} T={T} />
                ))}
              </div>
            </div>

            {/* channel table */}
            <div className="rounded-2xl overflow-hidden" style={{ background:T.card, border:`1px solid ${T.border}`, boxShadow:T.shadow }}>
              <div className="px-5 md:px-6 py-4" style={{ borderBottom:`1px solid ${T.border}` }}>
                <h2 className="text-base font-black" style={{ color:T.text }}>Channel Breakdown</h2>
                <p className="text-xs mt-0.5" style={{ color:T.faint }}>
                  All channels in your campaign_events — WhatsApp dry-run sends flagged clearly
                </p>
              </div>

              {loading ? (
                <div className="p-5 space-y-3">{Array(3).fill(null).map((_,i)=><Skel key={i} h={48} T={T} />)}</div>
              ) : channelRows.length===0 ? (
                <EmptyMini T={T} icon={Activity} text="No channels to show" pad />
              ) : (
                <>
                  {/* mobile cards */}
                  <div className="md:hidden divide-y" style={{ borderColor:T.borderSoft }}>
                    {channelRows.map((r) => {
                      const m = channelMeta(r.channel);
                      const dryOnly = r.sends_real===0 && r.sends_dry_run>0;
                      return (
                        <div key={r.channel} className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="flex items-center gap-2 font-black text-sm" style={{ color:T.text }}>
                              <m.Icon className="w-4 h-4" style={{ color:m.color }} /> {m.label}
                            </span>
                            {dryOnly && (
                              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold"
                                style={{ background:'rgba(251,191,36,0.14)', color:T.warn }}>
                                <FlaskConical className="w-2.5 h-2.5" /> DRY-RUN
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-4 gap-2">
                            {[
                              { l:'Real Sends', v:inr(r.sends_real),    c:T.text  },
                              { l:'Dry-run',    v:inr(r.sends_dry_run), c:T.faint },
                              { l:'Clicks',     v:inr(r.clicks),        c:m.color },
                              { l:'CTR',        v:r.ctr==null?'—':`${r.ctr}%`, c:T.sub },
                            ].map((s)=>(
                              <div key={s.l} className="text-center">
                                <div className="text-sm font-black" style={{ color:s.c }}>{s.v}</div>
                                <div className="text-[9px] font-bold uppercase tracking-wider mt-0.5" style={{ color:T.faint }}>{s.l}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm" style={{ minWidth:620 }}>
                      <thead>
                        <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                          {['Channel','Real Sends','Dry-run','Clicks','CTR'].map((c)=>(
                            <th key={c} className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-left"
                              style={{ color:T.faint, background:T.cardAlt }}>{c}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {channelRows.map((r) => {
                          const m = channelMeta(r.channel);
                          const dryOnly = r.sends_real===0 && r.sends_dry_run>0;
                          return (
                            <tr key={r.channel} className="attr-row" style={{ borderBottom:`1px solid ${T.borderSoft}` }}>
                              <td className="px-5 py-3.5">
                                <span className="inline-flex items-center gap-2 font-bold text-xs" style={{ color:T.text }}>
                                  <m.Icon className="w-4 h-4" style={{ color:m.color }} /> {m.label}
                                  {dryOnly && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold"
                                      style={{ background:'rgba(251,191,36,0.14)', color:T.warn }}>
                                      <FlaskConical className="w-2.5 h-2.5" /> DRY-RUN ONLY
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className="px-5 py-3.5 font-bold text-xs" style={{ color:T.text }}>{inr(r.sends_real)}</td>
                              <td className="px-5 py-3.5 text-xs" style={{ color:T.faint }}>{inr(r.sends_dry_run)}</td>
                              <td className="px-5 py-3.5 font-black text-sm" style={{ color:m.color }}>{inr(r.clicks)}</td>
                              <td className="px-5 py-3.5 font-bold text-xs" style={{ color:T.sub }}>{r.ctr==null?'—':`${r.ctr}%`}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* ===================== TRACKED LINKS TAB ===================== */}
        {tab==='tracked' && (
          <>
            {!loading && campaignClicks.length>0 && (
              <div className="rounded-2xl p-5 md:p-6 mb-4" style={{ background:T.card, border:`1px solid ${T.border}`, boxShadow:T.shadow }}>
                <h2 className="text-sm font-black mb-4" style={{ color:T.text }}>Top Campaigns by Clicks</h2>
                <ResponsiveContainer width="100%" height={Math.max(160, campaignClicks.length*38)}>
                  <BarChart data={campaignClicks} layout="vertical" margin={{ top:0, right:16, left:8, bottom:0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.grid} horizontal={false} />
                    <XAxis type="number" tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="campaign" tick={{ fill:T.sub, fontSize:11 }} width={140} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill:T.hover }} />
                    <Bar dataKey="clicks" radius={[0,5,5,0]} fill={T.accent} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="rounded-2xl overflow-hidden" style={{ background:T.card, border:`1px solid ${T.border}`, boxShadow:T.shadow }}>
              <div className="px-5 md:px-6 py-4" style={{ borderBottom:`1px solid ${T.border}` }}>
                <h2 className="text-base font-black" style={{ color:T.text }}>Tracked Link Details</h2>
                <p className="text-xs mt-0.5" style={{ color:T.faint }}>
                  {loading ? '—' : `${filteredLinks.length} link${filteredLinks.length!==1?'s':''} · click counts are lifetime`}
                </p>
              </div>

              {loading ? (
                <div className="p-5 space-y-3">{Array(5).fill(null).map((_,i)=><Skel key={i} h={40} T={T} />)}</div>
              ) : filteredLinks.length===0 ? (
                <EmptyMini T={T} icon={Link2} text={links.length===0 ? 'No tracked links yet — short link data will appear here once links are created' : 'No links match these filters'} pad />
              ) : (
                <>
                  {/* mobile */}
                  <div className="md:hidden divide-y" style={{ borderColor:T.borderSoft }}>
                    {filteredLinks.map((row,idx) => {
                      const m = channelMeta(row.channel);
                      return (
                        <div key={row.id??row._id??idx} className="p-4">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="font-mono text-xs font-bold" style={{ color:T.accent }}>{row.short_code||row.code||'—'}</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background:m.color+'22', color:m.color }}>{m.label}</span>
                          </div>
                          <div className="text-xs mb-1" style={{ color:T.sub }}>{row.campaign||row.campaign_name||'—'}</div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold" style={{ color:T.warn }}>{parseInt(row.clicks??row.click_count??0,10)} clicks</span>
                            <span className="text-[10px]" style={{ color:T.faint }}>{formatIST(row.created_at||row.createdAt)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* desktop */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm" style={{ minWidth:640 }}>
                      <thead>
                        <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                          {['Short Code','Campaign','Channel','Clicks','Created'].map((c)=>(
                            <th key={c} className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-left"
                              style={{ color:T.faint, background:T.cardAlt }}>{c}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLinks.map((row,idx) => {
                          const m = channelMeta(row.channel);
                          return (
                            <tr key={row.id??row._id??idx} className="attr-row" style={{ borderBottom:`1px solid ${T.borderSoft}` }}>
                              <td className="px-5 py-3.5 font-mono text-xs font-bold" style={{ color:T.accent }}>{row.short_code||row.code||'—'}</td>
                              <td className="px-5 py-3.5 text-xs" style={{ color:T.sub }}>{row.campaign||row.campaign_name||'—'}</td>
                              <td className="px-5 py-3.5">
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold"
                                  style={{ background:m.color+'22', color:m.color }}>
                                  <m.Icon className="w-3 h-3" /> {m.label}
                                </span>
                              </td>
                              <td className="px-5 py-3.5 font-black text-sm" style={{ color:T.warn }}>{parseInt(row.clicks??row.click_count??0,10)}</td>
                              <td className="px-5 py-3.5 text-xs whitespace-nowrap" style={{ color:T.faint }}>{formatIST(row.created_at||row.createdAt)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* ===================== ORGANIC CONVERSIONS TAB ===================== */}
        {tab==='organic' && (
          <>
            <div className="rounded-2xl p-4 mb-5 flex items-start gap-3"
              style={{ background:T.accentSoft, border:`1px solid ${T.accent}33` }}>
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color:T.accent }} />
              <p className="text-xs leading-relaxed" style={{ color:T.sub }}>
                <strong style={{ color:T.text }}>How organic is detected:</strong> a conversion is <em>organic</em> when the
                profile had <strong>no campaign send in the trailing 30 days</strong> before the conversion
                and no campaign/link/paid tag — earned outcomes no paid touch can claim.
                Other tools don't show this. Everything else is <em>attributed</em>.
              </p>
            </div>

            {loading ? (
              <div className="space-y-4"><Skel h={150} T={T} /><Skel h={240} T={T} /></div>
            ) : convTotals.conversions===0 ? (
              <OrganicEmpty T={T} />
            ) : (
              <>
                {/* donut + stat grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="rounded-2xl p-5 flex items-center gap-5"
                    style={{ background:T.card, border:`1px solid ${T.border}`, boxShadow:T.shadow }}>
                    <div className="relative" style={{ width:120, height:120, flexShrink:0 }}>
                      <Donut T={T} size={120} stroke={14} segments={[
                        { label:'Organic',    value:convTotals.organic_conversions, color:T.good },
                        { label:'Attributed', value:convTotals.paid_conversions,    color:T.accent },
                      ]} />
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="text-xl font-black" style={{ color:T.text }}>{convTotals.organic_share_pct}%</div>
                        <div className="text-[9px] font-bold uppercase tracking-wider" style={{ color:T.faint }}>Organic</div>
                      </div>
                    </div>
                    <div className="space-y-2.5 flex-1">
                      <LegendDot color={T.good}   label="Organic"    val={convTotals.organic_conversions} T={T} />
                      <LegendDot color={T.accent} label="Attributed" val={convTotals.paid_conversions}    T={T} />
                    </div>
                  </div>

                  <div className="rounded-2xl p-5 md:col-span-2 grid grid-cols-2 gap-4"
                    style={{ background:T.card, border:`1px solid ${T.border}`, boxShadow:T.shadow }}>
                    <ValueStat T={T} icon={Sprout}       label="Organic value"     value={convTotals.organic_value} accent={T.good} />
                    <ValueStat T={T} icon={Target}        label="Attributed value"  value={convTotals.paid_value}    accent={T.accent} />
                    <ValueStat T={T} icon={Sparkles}      label="Total conversions" value={convTotals.conversions}   count />
                    <ValueStat T={T} icon={IndianRupee}   label="Total value"       value={convTotals.value}         accent={T.text} />
                  </div>
                </div>

                {/* trend chart */}
                {conv?.trend?.length>0 && (
                  <div className="rounded-2xl p-5 md:p-6 mb-4"
                    style={{ background:T.card, border:`1px solid ${T.border}`, boxShadow:T.shadow }}>
                    <h2 className="text-sm font-black mb-4" style={{ color:T.text }}>Organic vs Attributed — Daily (IST)</h2>
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={conv.trend} margin={{ top:4, right:12, left:-10, bottom:4 }}>
                        <defs>
                          <linearGradient id="gOrg" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={T.good}   stopOpacity={0.35} />
                            <stop offset="100%" stopColor={T.good} stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gPaid" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={T.accent}   stopOpacity={0.35} />
                            <stop offset="100%" stopColor={T.accent} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false} />
                        <XAxis dataKey="date" tick={axisTick} axisLine={false} tickLine={false} minTickGap={24} />
                        <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                        <Tooltip contentStyle={tooltipStyle} cursor={{ stroke:T.border }} />
                        <Area type="monotone" dataKey="organic" name="Organic"    stroke={T.good}   strokeWidth={2} fill="url(#gOrg)"  />
                        <Area type="monotone" dataKey="paid"    name="Attributed" stroke={T.accent} strokeWidth={2} fill="url(#gPaid)" />
                      </AreaChart>
                    </ResponsiveContainer>
                    <div className="flex items-center gap-4 mt-3">
                      <Legend swatch={T.good}   label="Organic"    T={T} />
                      <Legend swatch={T.accent} label="Attributed" T={T} />
                    </div>
                  </div>
                )}

                {/* by type table */}
                {conv?.by_type?.length>0 && (
                  <div className="rounded-2xl overflow-hidden"
                    style={{ background:T.card, border:`1px solid ${T.border}`, boxShadow:T.shadow }}>
                    <div className="px-5 md:px-6 py-4" style={{ borderBottom:`1px solid ${T.border}` }}>
                      <h2 className="text-base font-black" style={{ color:T.text }}>Conversions by Type</h2>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" style={{ minWidth:480 }}>
                        <thead>
                          <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                            {['Type','Total','Organic','Value (₹)'].map((c)=>(
                              <th key={c} className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-left"
                                style={{ color:T.faint, background:T.cardAlt }}>{c}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {conv.by_type.map((r)=>(
                            <tr key={r.conversion_type} className="attr-row" style={{ borderBottom:`1px solid ${T.borderSoft}` }}>
                              <td className="px-5 py-3.5 font-bold text-xs capitalize" style={{ color:T.text }}>{r.conversion_type}</td>
                              <td className="px-5 py-3.5 font-black text-sm" style={{ color:T.text }}>{inr(r.conversions)}</td>
                              <td className="px-5 py-3.5 font-bold text-xs" style={{ color:T.good }}>{inr(r.organic_conversions)}</td>
                              <td className="px-5 py-3.5 font-bold text-xs" style={{ color:T.sub }}>₹{inr(r.value)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* phase 2 teaser */}
            <div className="rounded-2xl p-5 mt-5 flex items-start gap-3"
              style={{ background:T.cardAlt, border:`1px dashed ${T.border}` }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background:T.accentSoft }}>
                <Search className="w-4 h-4" style={{ color:T.accent }} />
              </div>
              <div>
                <div className="text-xs font-black mb-1" style={{ color:T.text }}>Organic Source Intelligence — Phase 2</div>
                <p className="text-xs leading-relaxed" style={{ color:T.faint }}>
                  SEO keyword → conversion mapping, referral source breakdown, and organic-vs-paid lift modelling.
                  Conversions tagged with <code style={{ color:T.accent }}>utm_source</code> / organic source will flow into this tab automatically.
                </p>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

/* ---- sub-components ---- */
function Legend({ swatch, label, T }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold" style={{ color:T.faint }}>
      <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background:swatch }} /> {label}
    </span>
  );
}
function LegendDot({ color, label, val, T }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0" style={{ background:color }} />
      <span className="text-xs font-bold" style={{ color:T.sub }}>{label}</span>
      <span className="text-xs font-black ml-auto" style={{ color:T.text }}>{val}</span>
    </div>
  );
}
function ValueStat({ icon:Icon, label, value, accent, count, T }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color:accent||T.faint }} />
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color:T.faint }}>{label}</span>
      </div>
      <div className="text-xl font-black" style={{ color:T.text }}>
        {count ? '' : '₹'}{(Number(value)||0).toLocaleString('en-IN')}
      </div>
    </div>
  );
}
function EmptyMini({ T, icon:Icon, text, pad }) {
  return (
    <div className={`flex flex-col items-center justify-center text-center px-4 ${pad?'py-16':'py-10'}`}>
      <Icon className="w-8 h-8 mb-2" style={{ color:T.fainter }} />
      <p className="text-sm font-semibold max-w-xs" style={{ color:T.faint }}>{text}</p>
    </div>
  );
}
function OrganicEmpty({ T }) {
  return (
    <div className="rounded-2xl p-6 md:p-10" style={{ background:T.card, border:`1px solid ${T.border}`, boxShadow:T.shadow }}>
      <div className="flex flex-col items-center text-center max-w-xl mx-auto">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background:T.accentSoft }}>
          <Sprout className="w-7 h-7" style={{ color:T.accent }} />
        </div>
        <h3 className="text-lg font-black mb-2" style={{ color:T.text }}>No conversions logged yet</h3>
        <p className="text-sm leading-relaxed mb-6" style={{ color:T.faint }}>
          Once you start recording business outcomes, this tab separates the ones your campaigns earned
          from the ones that arrived organically — the number most attribution tools quietly hide.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full mb-6">
          {[
            { n:'1', t:'Log a conversion', d:'POST when a booking / purchase happens' },
            { n:'2', t:'We trace touches',  d:'Check campaign sends in the 30d window' },
            { n:'3', t:'Organic surfaces',  d:'Untouched outcomes = organic' },
          ].map((s)=>(
            <div key={s.n} className="rounded-xl p-3.5" style={{ background:T.cardAlt, border:`1px solid ${T.borderSoft}` }}>
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black mb-2 mx-auto"
                style={{ background:T.accent, color:'#fff' }}>{s.n}</div>
              <div className="text-xs font-bold mb-0.5" style={{ color:T.text }}>{s.t}</div>
              <div className="text-[10px] leading-snug" style={{ color:T.faint }}>{s.d}</div>
            </div>
          ))}
        </div>
        <div className="w-full rounded-xl p-4 text-left font-mono text-[11px] leading-relaxed overflow-x-auto"
          style={{ background:T.cardAlt, border:`1px solid ${T.borderSoft}`, color:T.sub }}>
          <span style={{ color:T.good }}>POST</span> /api/v1/attribution-hub/conversions<br />
          {'{'} <span style={{ color:T.accent }}>"profile_id"</span>: "...",{' '}
          <span style={{ color:T.accent }}>"conversion_type"</span>: "appointment",<br />
          &nbsp;&nbsp;<span style={{ color:T.accent }}>"value"</span>: 1500,{' '}
          <span style={{ color:T.accent }}>"channel"</span>: null {'}'}
        </div>
      </div>
    </div>
  );
}